/**
 * Enhanced Base Service Class
 * Implements unified service patterns with advanced features
 * - Intelligent caching with multiple strategies
 * - Comprehensive error handling and retry logic
 * - Performance monitoring and metrics
 * - Request/response interceptors
 * - Type-safe validation
 */

import { consola } from "consola";
import { nanoid } from "nanoid";

import { createLogger } from "@/utils/logger";

import {
  ErrorHandler,
  ServiceErrorCode,
  UnifiedServiceError,
  ValidationError,
} from "./errors";
import { HttpClient, RequestConfig } from "./http-client";
import {
  createDefaultInterceptorManager,
  InterceptorManager,
} from "./interceptors";
import {
  IService,
  QueryParams,
  RequestContext,
  ServiceConfig,
  ServiceEvent,
  ServiceEventHandler,
  ServiceMetrics,
  ServiceResponse,
  ValidationResult,
  ValidationSchema,
} from "./types";

// Legacy interface support - will be removed in future versions
interface LegacyServiceConfig {
  name: string;
  client?: HttpClient;
  enableLogging?: boolean;
  retryAttempts?: number;
  retryDelay?: number;
}

export interface RetryOptions {
  attempts: number;
  delay: number;
  shouldRetry?: (error: UnifiedServiceError) => boolean;
}

/**
 * Enhanced base class for all services with unified patterns
 * Provides comprehensive functionality including caching, validation, and monitoring
 */
export abstract class BaseService implements IService {
  protected readonly client: HttpClient;
  protected readonly logger: ReturnType<typeof createLogger>;
  protected readonly interceptorManager: InterceptorManager;
  protected readonly metrics: ServiceMetrics;
  private readonly eventHandlers: Set<ServiceEventHandler> = new Set();
  private readonly contexts: Map<string, RequestContext> = new Map();

  public readonly name: string;
  public readonly config: ServiceConfig;

  constructor(config: ServiceConfig | LegacyServiceConfig) {
    // Handle legacy config format
    const normalizedConfig: ServiceConfig = this.normalizeConfig(config);

    this.name = normalizedConfig.name;
    this.config = normalizedConfig;
    this.client = new HttpClient({
      baseURL: normalizedConfig.baseURL,
      timeout: normalizedConfig.timeout,
      headers: normalizedConfig.headers,
      credentials: normalizedConfig.credentials,
    });
    this.logger = consola.withTag(`service-${normalizedConfig.name}`);
    this.interceptorManager = createDefaultInterceptorManager();

    // Initialize metrics
    this.metrics = {
      requests: { total: 0, successful: 0, failed: 0, averageResponseTime: 0 },
      cache: { hits: 0, misses: 0, hitRate: 0 },
      errors: { byCode: {} as Record<ServiceErrorCode, number>, total: 0 },
      uptime: Date.now(),
      lastHealthCheck: new Date().toISOString(),
    };

    // Log service initialization
    // this.logger.info("Service initialized", {
    //   name: this.name,
    //   config: this.sanitizeConfig(normalizedConfig),
    // });
  }

  /**
   * Normalize legacy config to new format
   */
  private normalizeConfig(
    config: ServiceConfig | LegacyServiceConfig,
  ): ServiceConfig {
    if ("retryAttempts" in config) {
      // Legacy format
      return {
        name: config.name,
        baseURL: process.env.NEXT_PUBLIC_BASE_URL,
        timeout:
          process.env.NODE_ENV === "development" ||
          process.env.NEXT_PUBLIC_ENV === "development"
            ? parseInt(
                process.env.NEXT_PUBLIC_API_TIMEOUT ||
                  process.env.API_TIMEOUT ||
                  "1500",
              ) || 1500
            : 30000,
        retries: config.retryAttempts || 3,
        retryDelay: config.retryDelay || 1000,
        enableLogging: config.enableLogging ?? true,
        enableMetrics: true,
        cache: {
          enabled: false,
          defaultTtl: 5 * 60 * 1000,
          strategy: "cache-first",
        },
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      };
    }
    return config as ServiceConfig;
  }

  /**
   * Sanitize config for logging (remove sensitive data)
   */
  private sanitizeConfig(config: ServiceConfig): Partial<ServiceConfig> {
    const { headers, ...safeConfig } = config;
    return {
      ...safeConfig,
      headers: headers ? Object.keys(headers) : undefined,
    };
  }

  /**
   * Default retry logic for failed requests
   */
  private defaultShouldRetry = (error: UnifiedServiceError): boolean => {
    return ErrorHandler.shouldRetry(error, 1, this.config.retries || 3);
  };

  /**
   * Sleep utility for retry delays
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Execute request with enhanced retry logic
   */
  protected async executeWithRetry<T>(
    operation: () => Promise<ServiceResponse<T>>,
    customRetryOptions?: Partial<RetryOptions>,
  ): Promise<ServiceResponse<T>> {
    const retries = this.config.retries || 3;
    const baseDelay = this.config.retryDelay || 1000;
    const options = {
      attempts: retries,
      delay: baseDelay,
      shouldRetry: this.defaultShouldRetry,
      ...customRetryOptions,
    };

    let lastError: UnifiedServiceError;
    const startTime = Date.now();

    for (let attempt = 1; attempt <= options.attempts; attempt++) {
      try {
        const result = await operation();

        // Update metrics on success
        this.updateMetrics("success", Date.now() - startTime);

        if (this.config.enableLogging && attempt > 1) {
          this.logger.info(`Request succeeded on attempt ${attempt}`, {
            service: this.name,
            attempt,
            duration: Date.now() - startTime,
          });
        }

        return result;
      } catch (error) {
        lastError = ErrorHandler.normalize(
          error,
          `${this.name} operation attempt ${attempt}`,
        );

        if (attempt === options.attempts || !options.shouldRetry?.(lastError)) {
          break;
        }

        const delay = ErrorHandler.getRetryDelay(attempt, baseDelay);

        if (this.config.enableLogging) {
          this.logger.warn(
            `Request failed on attempt ${attempt}, retrying in ${delay}ms...`,
            {
              service: this.name,
              error: lastError.code,
              attempt,
              nextAttempt: attempt + 1,
              maxAttempts: options.attempts,
              delay,
            },
          );
        }

        await this.sleep(delay);
      }
    }

    // Update metrics on failure
    this.updateMetrics("failure", Date.now() - startTime, lastError);

    if (this.config.enableLogging) {
      this.logger.error(`Request failed after ${options.attempts} attempts`, {
        service: this.name,
        error: lastError.toJSON(),
        totalDuration: Date.now() - startTime,
      });
    }

    // Process error through interceptors
    const processedError =
      await this.interceptorManager.processError(lastError);
    throw processedError;
  }

  /**
   * Enhanced error handling with interceptors and metrics
   */
  protected async handleError(
    error: unknown,
    context?: string,
  ): Promise<never> {
    const normalizedError = ErrorHandler.normalize(error, context);
    normalizedError.service = this.name;

    // Update error metrics
    this.updateErrorMetrics(normalizedError);

    // Log error appropriately
    ErrorHandler.log(normalizedError, this.logger);

    // Emit error event
    this.emitEvent({
      type: "error",
      service: this.name,
      timestamp: new Date().toISOString(),
      data: normalizedError.toJSON(),
    });

    // Process through interceptors
    const processedError =
      await this.interceptorManager.processError(normalizedError);

    throw processedError;
  }

  /**
   * Enhanced parameter validation with schema support
   */
  protected validateRequired(
    params: Record<string, any>,
    requiredFields: string[],
  ): void {
    const missing = requiredFields.filter(
      (field) =>
        params[field] === undefined ||
        params[field] === null ||
        params[field] === "",
    );

    if (missing.length > 0) {
      throw new ValidationError(
        `Missing required parameters: ${missing.join(", ")}`,
        missing.map((field) => ({
          field,
          message: "Required field is missing",
          value: params[field],
        })),
        {
          service: this.name,
          context: "parameter validation",
          details: { missing, provided: Object.keys(params) },
        },
      );
    }
  }

  /**
   * Advanced schema-based validation
   */
  protected validateSchema(
    params: Record<string, any>,
    schema: ValidationSchema,
  ): ValidationResult {
    const errors: Array<{ field: string; message: string; value?: any }> = [];

    for (const rule of schema.rules) {
      const value = params[rule.field];

      // Required field check
      if (
        rule.required &&
        (value === undefined || value === null || value === "")
      ) {
        errors.push({
          field: rule.field,
          message: `Field '${rule.field}' is required`,
          value,
        });
        continue;
      }

      // Skip validation for optional empty fields
      if (
        !rule.required &&
        (value === undefined || value === null || value === "")
      ) {
        continue;
      }

      // Type validation
      if (rule.type && !this.validateType(value, rule.type)) {
        errors.push({
          field: rule.field,
          message: `Field '${rule.field}' must be of type ${rule.type}`,
          value,
        });
        continue;
      }

      // Min/Max validation for numbers and strings
      if (rule.min !== undefined) {
        const length = typeof value === "string" ? value.length : value;
        if (length < rule.min) {
          errors.push({
            field: rule.field,
            message: `Field '${rule.field}' must be at least ${rule.min}`,
            value,
          });
        }
      }

      if (rule.max !== undefined) {
        const length = typeof value === "string" ? value.length : value;
        if (length > rule.max) {
          errors.push({
            field: rule.field,
            message: `Field '${rule.field}' must be at most ${rule.max}`,
            value,
          });
        }
      }

      // Pattern validation
      if (
        rule.pattern &&
        typeof value === "string" &&
        !rule.pattern.test(value)
      ) {
        errors.push({
          field: rule.field,
          message: `Field '${rule.field}' format is invalid`,
          value,
        });
      }

      // Custom validation
      if (rule.custom) {
        const result = rule.custom(value);
        if (result !== true) {
          errors.push({
            field: rule.field,
            message:
              typeof result === "string"
                ? result
                : `Field '${rule.field}' is invalid`,
            value,
          });
        }
      }
    }

    // Check for unknown fields
    if (!schema.allowUnknown) {
      const allowedFields = schema.rules.map((r) => r.field);
      const unknownFields = Object.keys(params).filter(
        (key) => !allowedFields.includes(key),
      );

      for (const field of unknownFields) {
        errors.push({
          field,
          message: `Unknown field '${field}' is not allowed`,
          value: params[field],
        });
      }
    }

    const result: ValidationResult = {
      valid: errors.length === 0,
      errors,
    };

    if (!result.valid) {
      throw new ValidationError(
        `Validation failed for ${errors.length} field(s)`,
        errors,
        { service: this.name, context: "schema validation" },
      );
    }

    return result;
  }

  /**
   * Type validation helper
   */
  private validateType(value: any, expectedType: string): boolean {
    switch (expectedType) {
      case "string":
        return typeof value === "string";
      case "number":
        return typeof value === "number" && !isNaN(value);
      case "boolean":
        return typeof value === "boolean";
      case "array":
        return Array.isArray(value);
      case "object":
        return (
          typeof value === "object" && value !== null && !Array.isArray(value)
        );
      default:
        return true;
    }
  }

  /**
   * Legacy logging methods - kept for backward compatibility
   */
  protected logOperationStart(operation: string, params?: any): void {
    if (!this.config.enableLogging) return;

    this.logger.debug(`Starting ${operation}`, {
      operation,
      params: params ? Object.keys(params) : undefined,
      service: this.name,
    });
  }

  /**
   * Legacy logging methods - kept for backward compatibility
   */
  protected logOperationSuccess(operation: string, result?: any): void {
    if (!this.config.enableLogging) return;

    this.logger.debug(`Completed ${operation}`, {
      operation,
      hasResult: !!result,
      service: this.name,
    });
  }

  /**
   * Cleanup resources when service is destroyed
   */
  destroy(): void {
    this.eventHandlers.clear();
    this.contexts.clear();
    this.logger.info("Service destroyed", { service: this.name });
  }

  /**
   * Enhanced GET request wrapper with caching and interceptors
   */
  protected async get<T>(
    endpoint: string,
    params?: QueryParams,
    options?: {
      cache?: boolean;
      cacheTtl?: number;
      cacheTags?: string[];
      skipCache?: boolean;
      timeout?: number;
      headers?: Record<string, string>;
    },
  ): Promise<T> {
    const operation = `GET ${endpoint}`;
    const context = this.createContext(operation, { params, options });

    try {
      // Direct request (cache now handled by Next.js)
      const response = await this.executeRequest<T>(
        "GET",
        endpoint,
        undefined,
        {
          params,
          timeout: options?.timeout,
          headers: options?.headers,
        },
      );

      this.completeContext(context.requestId);
      return response.data;
    } catch (error) {
      this.completeContext(context.requestId);
      await this.handleError(error, operation);
    }
  }

  /**
   * Enhanced POST request wrapper
   */
  protected async post<T>(
    endpoint: string,
    body?: any,
    options?: {
      timeout?: number;
      headers?: Record<string, string>;
    },
  ): Promise<T> {
    const operation = `POST ${endpoint}`;
    const context = this.createContext(operation, { body, options });

    try {
      const response = await this.executeRequest<T>("POST", endpoint, body, {
        timeout: options?.timeout,
        headers: options?.headers,
      });

      this.completeContext(context.requestId);
      return response.data;
    } catch (error) {
      this.completeContext(context.requestId);
      await this.handleError(error, operation);
    }
  }

  /**
   * Enhanced PUT request wrapper
   */
  protected async put<T>(
    endpoint: string,
    body?: any,
    options?: {
      timeout?: number;
      headers?: Record<string, string>;
    },
  ): Promise<T> {
    const operation = `PUT ${endpoint}`;
    const context = this.createContext(operation, { body, options });

    try {
      const response = await this.executeRequest<T>("PUT", endpoint, body, {
        timeout: options?.timeout,
        headers: options?.headers,
      });

      this.completeContext(context.requestId);
      return response.data;
    } catch (error) {
      this.completeContext(context.requestId);
      await this.handleError(error, operation);
    }
  }

  /**
   * Enhanced PATCH request wrapper
   */
  protected async patch<T>(
    endpoint: string,
    body?: any,
    options?: {
      timeout?: number;
      headers?: Record<string, string>;
    },
  ): Promise<T> {
    const operation = `PATCH ${endpoint}`;
    const context = this.createContext(operation, { body, options });

    try {
      const response = await this.executeRequest<T>("PATCH", endpoint, body, {
        timeout: options?.timeout,
        headers: options?.headers,
      });

      this.completeContext(context.requestId);
      return response.data;
    } catch (error) {
      this.completeContext(context.requestId);
      await this.handleError(error, operation);
    }
  }

  /**
   * Enhanced DELETE request wrapper
   */
  protected async delete<T>(
    endpoint: string,
    body?: Record<string, any>,
    options?: {
      timeout?: number;
      headers?: Record<string, string>;
    },
  ): Promise<T> {
    const operation = `DELETE ${endpoint}`;
    const context = this.createContext(operation, { body, options });

    try {
      const response = await this.executeRequest<T>("DELETE", endpoint, body, {
        timeout: options?.timeout,
        headers: options?.headers,
      });

      this.completeContext(context.requestId);
      return response.data;
    } catch (error) {
      this.completeContext(context.requestId);
      await this.handleError(error, operation);
    }
  }

  /**
   * Core request execution method with interceptors
   */
  private async executeRequest<T>(
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    endpoint: string,
    body?: any,
    config?: Omit<RequestConfig, "method" | "body">,
  ): Promise<ServiceResponse<T>> {
    const requestConfig = {
      method,
      url: endpoint,
      body,
      ...config,
    };

    // Process request through interceptors
    const processedConfig =
      await this.interceptorManager.processRequest(requestConfig);

    // Execute with retry logic
    return this.executeWithRetry(async () => {
      const response = await this.client.request<T>(processedConfig.url, {
        method: processedConfig.method,
        body: processedConfig.body,
        headers: processedConfig.headers,
        params: processedConfig.params,
        timeout: processedConfig.timeout,
        cache: processedConfig.cache,
        credentials: processedConfig.credentials,
      });

      // Transform to ServiceResponse format
      const serviceResponse: ServiceResponse<T> = {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
        meta: response.meta,
      };

      // Process response through interceptors
      return this.interceptorManager.processResponse(serviceResponse);
    });
  }

  /**
   * Generate cache key for request
   */
  private generateCacheKey(endpoint: string, params?: QueryParams): string {
    const sortedParams = params
      ? JSON.stringify(params, Object.keys(params).sort())
      : "";
    return `${endpoint}:${sortedParams}`;
  }

  /**
   * Enhanced health check with detailed diagnostics
   */
  async healthCheck(): Promise<{
    status: "healthy" | "unhealthy";
    details?: any;
  }> {
    const startTime = Date.now();

    try {
      // Basic connectivity check
      await this.client.get("/health", {
        cache: "no-store",
        timeout: 3000, // 3 second timeout for health check
      });

      const duration = Date.now() - startTime;
      this.metrics.lastHealthCheck = new Date().toISOString();

      const details = {
        service: this.name,
        timestamp: this.metrics.lastHealthCheck,
        duration,
        uptime: Date.now() - this.metrics.uptime,
        metrics: this.metrics,
        cache: this.getCacheStats(),
      };

      this.logger.debug("Health check passed", details);

      return {
        status: "healthy",
        details,
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      const normalizedError = ErrorHandler.normalize(error);

      const details = {
        service: this.name,
        timestamp: new Date().toISOString(),
        duration,
        error: normalizedError.toJSON(),
        uptime: Date.now() - this.metrics.uptime,
        metrics: this.metrics,
      };

      this.logger.warn(`Health check failed for ${this.name}`, details);

      return {
        status: "unhealthy",
        details,
      };
    }
  }

  /**
   * Get service metrics
   */
  async getMetrics(): Promise<ServiceMetrics> {
    return {
      ...this.metrics,
      cache: this.getCacheStats(),
    };
  }

  /**
   * Clear service cache - deprecated, now handled by Next.js cache tags
   */
  async clearCache(pattern?: string): Promise<void> {
    this.logger.info("Cache clearing now handled by Next.js revalidateTag", {
      service: this.name,
      pattern,
    });
  }

  /**
   * Get cache statistics - deprecated
   */
  private getCacheStats() {
    return { hits: 0, misses: 0, hitRate: 0 };
  }

  /**
   * Update service metrics
   */
  private updateMetrics(
    type: "success" | "failure",
    duration: number,
    error?: UnifiedServiceError,
  ): void {
    this.metrics.requests.total++;

    if (type === "success") {
      this.metrics.requests.successful++;
    } else {
      this.metrics.requests.failed++;
      if (error) {
        this.updateErrorMetrics(error);
      }
    }

    // Update average response time
    const totalRequests = this.metrics.requests.total;
    this.metrics.requests.averageResponseTime =
      (this.metrics.requests.averageResponseTime * (totalRequests - 1) +
        duration) /
      totalRequests;
  }

  /**
   * Update error metrics
   */
  private updateErrorMetrics(error: UnifiedServiceError): void {
    this.metrics.errors.total++;

    if (!this.metrics.errors.byCode[error.code]) {
      this.metrics.errors.byCode[error.code] = 0;
    }
    this.metrics.errors.byCode[error.code]++;
  }

  /**
   * Emit service event
   */
  private emitEvent(event: ServiceEvent): void {
    for (const handler of this.eventHandlers) {
      try {
        handler(event);
      } catch (error) {
        this.logger.warn("Event handler failed", {
          error,
          eventType: event.type,
        });
      }
    }
  }

  /**
   * Add event handler
   */
  addEventListener(handler: ServiceEventHandler): void {
    this.eventHandlers.add(handler);
  }

  /**
   * Remove event handler
   */
  removeEventListener(handler: ServiceEventHandler): void {
    this.eventHandlers.delete(handler);
  }

  /**
   * Create request context for tracking
   */
  protected createContext(
    operation: string,
    metadata?: Record<string, any>,
  ): RequestContext {
    const context: RequestContext = {
      requestId: nanoid(),
      service: this.name,
      operation,
      startTime: Date.now(),
      metadata,
    };

    this.contexts.set(context.requestId, context);
    return context;
  }

  /**
   * Complete request context
   */
  protected completeContext(requestId: string): void {
    this.contexts.delete(requestId);
  }
}

/**
 * Service registry for managing service instances
 * Implements Singleton pattern for service management
 */
export class ServiceRegistry {
  private static instance: ServiceRegistry;
  private services: Map<string, BaseService> = new Map();

  private constructor() {}

  static getInstance(): ServiceRegistry {
    if (!ServiceRegistry.instance) {
      ServiceRegistry.instance = new ServiceRegistry();
    }
    return ServiceRegistry.instance;
  }

  register(service: BaseService): void {
    const serviceName = (service as any).serviceName;
    this.services.set(serviceName, service);
  }

  get(serviceName: string): BaseService | undefined {
    return this.services.get(serviceName);
  }

  async healthCheckAll(): Promise<Record<string, any>> {
    const results: Record<string, any> = {};

    for (const [name, service] of this.services) {
      try {
        results[name] = await service.healthCheck();
      } catch (error) {
        results[name] = {
          service: name,
          status: "unhealthy",
          error: error instanceof Error ? error.message : "Unknown error",
          timestamp: new Date().toISOString(),
        };
      }
    }

    return results;
  }
}

// Export singleton instance
export const serviceRegistry = ServiceRegistry.getInstance();
